import React, { useState, useEffect } from "react";
import { trackInitiateCheckout, trackPurchase } from "../../lib/pixel";
import {
  Package,
  CheckCircle,
  Truck,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Wrench,
} from "lucide-react";
import useStore from "../../store/useStore";
import pb from "../../lib/pocketbase";

const CheckoutPage = ({ onNavigate }) => {
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState("standard");
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    country: "Lebanon",
    address: "",
    orderNotes: "",
  });

  const cart = useStore((state) => state.cart);
  const clearCart = useStore((state) => state.clearCart);

  const baseSubtotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const resizingTotal = cart.reduce(
    (sum, item) => sum + (item.customization?.resizingFee || 0) * item.quantity,
    0
  );
  const cartTotal = baseSubtotal + resizingTotal;

  const deliveryFees = {
    standard: 6,
    express: 9,
  };

  const isDomestic =
    (formData.country || "").trim().toLowerCase() === "lebanon";

  const getItemSku = (item) =>
    item.sku || `MDB-${String(item.id).padStart(4, "0")}`;

  const selectedDeliveryFee = deliveryFees[deliveryMethod];
  const effectiveDeliveryFee = isDomestic ? selectedDeliveryFee : 1;
  const orderTotal = cartTotal + effectiveDeliveryFee;

  useEffect(() => {
    if (cart.length === 0) return;
    trackInitiateCheckout(
      cart.map((item) => ({ sku: getItemSku(item), quantity: item.quantity })),
      cartTotal
    );
    // Once per visit to checkout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync payment method with country rules
  useEffect(() => {
    if (!isDomestic && paymentMethod !== "wish_omt") {
      setPaymentMethod("wish_omt");
    }
  }, [isDomestic, paymentMethod]);

  // Calculate estimated delivery date
  const getEstimatedDeliveryDate = (method) => {
    const today = new Date();
    const daysToAdd = method === "standard" ? 7 : 3;
    const deliveryDate = new Date(today);
    deliveryDate.setDate(today.getDate() + daysToAdd);
    return deliveryDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.country.trim()) newErrors.country = "Country is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";

    // Optional email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePlaceOrder = async () => {
    // Check if cart is empty
    if (cart.length === 0) {
      alert("Your cart is empty!");
      return;
    }

    // Validate form - STOP if validation fails
    const isValid = validateForm();

    if (!isValid) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setIsSubmitting(true);
    // Force payment method for international orders
    const normalizedCountry = (formData.country || "").trim().toLowerCase();
    const isInternational = normalizedCountry !== "lebanon";
    const normalizedPayment =
      isInternational && paymentMethod !== "wish_omt"
        ? "wish_omt"
        : paymentMethod;

    try {
      // Prepare products data
      const productsData = cart.map((item) => ({
        sku: getItemSku(item),
        productId: item.id,
        productName: item.name,
        productPrice: item.price,
        quantity: item.quantity,
        category: item.gender,
        metal: item.metal,
        selectedSize: item.selectedSize || "N/A",
        needsResizing: item.customization?.needsResizing || false,
        resizingType: item.customization?.resizingType || "N/A",
        customSize: item.customization?.customSize || "N/A", // NEW: Custom size
        resizingFee: item.customization?.resizingFee || 0,
        image: item.images?.[0],
      }));

      // Create HTML summary
      const summary = `
<h3>Products:</h3>
<ul>
  ${cart
    .map((item) => {
      const resizingText = item.customization?.needsResizing
        ? ` - Resizing: Size ${item.selectedSize} → ${item.customization.customSize} (${item.customization.resizingType}) (+$${item.customization.resizingFee})`
        : "";
      return `<li>${item.name}${
        item.selectedSize ? ` (Size: ${item.selectedSize})` : ""
      }${resizingText} - ${item.metal} (Quantity: ${item.quantity}) - $${(
        (item.price + (item.customization?.resizingFee || 0)) *
        item.quantity
      ).toLocaleString()} | SKU: ${getItemSku(item)}</li>`;
    })
    .join("")}
</ul>
</ul>

<h3>Customer Information:</h3>
<ul>
  <li><strong>Name:</strong> ${formData.firstName} ${formData.lastName}</li>
  <li><strong>Phone:</strong> ${formData.phone}</li>
  ${formData.email ? `<li><strong>Email:</strong> ${formData.email}</li>` : ""}
</ul>

<h3>Shipping Details:</h3>
<ul>
  <li><strong>City:</strong> ${formData.city}</li>
  <li><strong>Address:</strong> ${formData.address}</li>
  <li><strong>Shipping Method:</strong> ${
    deliveryMethod === "standard" ? "Standard Delivery" : "Express Delivery"
  }</li>
  <li><strong>Country:</strong> ${formData.country}</li>
  ${
    formData.orderNotes
      ? `<li><strong>Order Notes:</strong> ${formData.orderNotes}</li>`
      : ""
  }
</ul>

<h3>Order Summary:</h3>
<ul>
  <li><strong>Subtotal:</strong> $${baseSubtotal.toLocaleString()}</li>
  ${
    resizingTotal > 0
      ? `<li><strong>Resizing Fee:</strong> $${resizingTotal.toLocaleString()}</li>`
      : ""
  }
  <li><strong>Delivery Fee:</strong> ${
    isDomestic
      ? `$${effectiveDeliveryFee.toFixed(2)}`
      : "TBD (we will coordinate)"
  }</li>
  <li><strong>Total:</strong> $${orderTotal.toLocaleString()}</li>
  <li><strong>Payment:</strong> ${
    normalizedPayment === "cod"
      ? "Cash on Delivery"
      : "Wish Money / OMT (Prepaid)"
  }</li>
</ul>
${
  formData.orderNotes
    ? `<p><strong>Note:</strong> ${formData.orderNotes}</p>`
    : ""
}
${
  !isDomestic
    ? `<p><strong>International:</strong> We will contact you to coordinate delivery time and fee. Prepaid via Wish Money / OMT.</p>`
    : ""
}
`.trim();

      // Prepare order data for PocketBase
      const orderData = {
        customerName: `${formData.firstName} ${formData.lastName}`,
        email: formData.email || "no-email@provided.com",
        phone: formData.phone,
        city: formData.city,
        country: formData.country,
        address: formData.address,
        sku: cart.map((item) => getItemSku(item)).join(", "),
        shippingMethod:
          isDomestic && deliveryMethod === "standard"
            ? "Standard Delivery"
            : isDomestic
            ? "Express Delivery"
            : "International - coordinated",
        deliveryNote: isDomestic
          ? ""
          : "International order - we will contact you to coordinate delivery time and fee. Prepaid via Wish Money / OMT.",
        orderNotes: formData.orderNotes || "No notes",
        products: productsData,
        subtotal: baseSubtotal,
        resizingFee: resizingTotal,
        deliveryFee: effectiveDeliveryFee,
        totalPrice: orderTotal,
        orderStatus: "pending",
        paymentMethod: normalizedPayment,
        summary: summary,
      };

      // Submit to PocketBase
      const record = await pb.collection("mdb_orders").create(orderData);

      // Store order details for confirmation screen
      setOrderDetails({
        orderId: record.id,
        customerName: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        address: formData.address,
        sku: cart.map((item) => getItemSku(item)).join(", "),
        items: cart.map((item) => ({ ...item, sku: getItemSku(item) })),
        subtotal: baseSubtotal,
        resizingFee: resizingTotal,
        deliveryFee: effectiveDeliveryFee,
        total: orderTotal,
        deliveryMethod:
          isDomestic && deliveryMethod === "standard"
            ? "Standard Delivery (5-7 days)"
            : isDomestic
            ? "Express Delivery (2-3 days)"
            : "International - we will contact you",
        estimatedDelivery: isDomestic
          ? getEstimatedDeliveryDate(deliveryMethod)
          : "We will contact you to coordinate delivery time",
        orderDate: new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        paymentMethod: normalizedPayment,
        country: formData.country,
        deliveryNote: !isDomestic
          ? "International order - we will contact you to coordinate delivery time and fee. Prepaid via Wish Money / OMT."
          : "",
      });

      setOrderPlaced(true);
      clearCart();

      trackPurchase(
        cart.map((item) => ({ sku: getItemSku(item), quantity: item.quantity })),
        orderTotal
      );
    } catch (error) {
      console.error("❌ Error placing order:", error);
      console.error("Error details:", error.response?.data || error.message);

      // The stock hook rejects with a plain, customer-readable sentence
      // ("Only 1 left of X (size 20)."). Show that rather than a JSON dump.
      const serverMessage = error?.response?.message || error?.data?.message;
      const isStockIssue =
        error?.status === 400 && serverMessage && !error?.response?.data;

      if (isStockIssue) {
        alert(
          `${serverMessage}\n\nPlease adjust your cart and try again — someone may have just bought it.`
        );
      } else {
        let errorMessage = "There was an error placing your order. ";
        if (serverMessage) errorMessage += `\n\n${serverMessage}`;
        else if (error.message) errorMessage += `\n\nError: ${error.message}`;
        errorMessage +=
          "\n\nPlease try again, or contact us if it keeps happening.";
        alert(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orderPlaced && orderDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f5f3f9] to-[#e8e4f3] flex items-center justify-center p-4">
        <div className="w-full max-w-3xl overflow-hidden bg-white shadow-2xl rounded-3xl">
          {/* Success Header */}
          <div className="p-8 text-center bg-gradient-to-r from-green-500 to-green-600">
            <div className="flex items-center justify-center w-20 h-20 mx-auto mb-4 bg-white rounded-full">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="mb-2 font-serif text-4xl text-white">Thank You!</h2>
            <p className="text-lg text-green-50">
              Your order has been successfully placed
            </p>
          </div>

          {/* Order Details */}
          <div className="p-8">
            {/* Order Info Cards */}
            <div className="grid gap-4 mb-6 md:grid-cols-2">
              {/* Customer Info */}
              <div className="p-4 rounded-lg bg-gray-50">
                <h3 className="flex items-center mb-3 text-sm font-semibold text-gray-600">
                  <Package className="w-4 h-4 mr-2" />
                  Customer Details
                </h3>
                <p className="mb-2 font-medium text-gray-900">
                  {orderDetails.customerName}
                </p>
                {orderDetails.email && (
                  <p className="flex items-center mb-1 text-sm text-gray-600">
                    <Mail className="w-4 h-4 mr-2" />
                    {orderDetails.email}
                  </p>
                )}
                <p className="flex items-center mb-1 text-sm text-gray-600">
                  <Phone className="w-4 h-4 mr-2" />
                  {orderDetails.phone}
                </p>
                <p className="flex items-center text-sm text-gray-600">
                  <MapPin className="w-4 h-4 mr-2" />
                  {orderDetails.city}, {orderDetails.country}
                </p>
              </div>

              {/* Delivery Info */}
              <div className="p-4 rounded-lg bg-gray-50">
                <h3 className="flex items-center mb-3 text-sm font-semibold text-gray-600">
                  <Truck className="w-4 h-4 mr-2" />
                  Delivery Details
                </h3>
                <p className="mb-2 font-medium text-gray-900">
                  {orderDetails.deliveryMethod}
                </p>
                {orderDetails.deliveryNote && (
                  <p className="text-sm text-gray-600">
                    {orderDetails.deliveryNote}
                  </p>
                )}
                <p className="flex items-center mb-1 text-sm text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  Est. Delivery: {orderDetails.estimatedDelivery}
                </p>
                <p className="text-sm text-gray-600">
                  Order Date: {orderDetails.orderDate}
                </p>
                <p className="text-sm text-gray-600">
                  Payment:{" "}
                  {orderDetails.paymentMethod === "cod"
                    ? "Cash on Delivery"
                    : "Wish Money / OMT (prepaid)"}
                </p>
              </div>
            </div>

            {/* Order ID */}
            <div className="p-4 mb-6 border-2 border-gray-300 border-dashed rounded-lg bg-gray-50">
              <p className="mb-1 text-xs text-center text-gray-500">
                Order Reference Number
              </p>
              <p className="font-mono text-sm font-bold text-center text-gray-900 select-all">
                #{orderDetails.orderId}
              </p>
              {orderDetails.sku && (
                <p className="mt-1 text-xs text-center text-gray-500">
                  SKUs: {orderDetails.sku}
                </p>
              )}
            </div>

            {/* Items Ordered */}
            <div className="mb-6">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Items Ordered
              </h3>
              <div className="space-y-3 overflow-y-auto max-h-60">
                {orderDetails.items.map((item, index) => (
                  <div
                    key={`${item.id}-${item.selectedSize || index}`} // UPDATED: Better key
                    className="flex gap-4 p-3 rounded-lg bg-gray-50"
                  >
                    <img
                      src={item.tinyThumbs?.[0] || item.images?.[0]}
                      alt={item.name}
                      className="object-cover w-16 h-16 rounded-lg"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.name}</p>
                      {item.sku && (
                        <p className="font-mono text-xs text-gray-500">
                          SKU: {item.sku}
                        </p>
                      )}
                      <p className="text-sm text-gray-600">{item.metal}</p>
                      {/* NEW: Display size in confirmation */}
                      {item.selectedSize && (
                        <p className="text-sm text-gray-600">
                          Size: {item.selectedSize}
                        </p>
                      )}
                      {item.customization?.needsResizing && (
                        <p className="flex items-center gap-1 text-sm text-blue-600">
                          <Wrench className="w-3 h-3" />
                          Resize: Size {item.selectedSize} →{" "}
                          {item.customization.customSize} (
                          {item.customization.resizingType === "tighten"
                            ? "Tighten"
                            : "Widen"}
                          )
                        </p>
                      )}
                      <p className="text-sm text-gray-600">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <p className="font-semibold text-gray-900">
                      ${(item.price * item.quantity).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex justify-between mb-2 text-gray-600">
                <span>Subtotal</span>
                <span className="font-semibold">
                  ${orderDetails.subtotal.toLocaleString()}
                </span>
              </div>
              {orderDetails.resizingFee > 0 && (
                <div className="flex justify-between mb-2 text-gray-600">
                  <span>Resizing Fee</span>
                  <span className="font-semibold">
                    ${orderDetails.resizingFee.toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between mb-2 text-gray-600">
                <span>Delivery Fee</span>
                <span className="font-semibold">
                  {orderDetails.deliveryNote
                    ? "TBD"
                    : `$${orderDetails.deliveryFee.toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between mb-2 text-gray-600">
                <span>Payment</span>
                <span className="font-semibold">
                  {orderDetails.paymentMethod === "cod"
                    ? "Cash on Delivery"
                    : "Wish Money / OMT"}
                </span>
              </div>
              <div className="flex justify-between pt-3 text-xl font-bold text-gray-900 border-t border-gray-200">
                <span>Total Paid</span>
                <span className="text-green-600">
                  ${orderDetails.total.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="p-4 mt-6 rounded-lg bg-gray-50">
              <h3 className="mb-2 text-sm font-semibold text-gray-600">
                Shipping Address
              </h3>
              <p className="text-sm text-gray-900">{orderDetails.address}</p>
              <p className="text-sm text-gray-900">
                {orderDetails.city}, {orderDetails.country}
              </p>
            </div>

            {/* Action Button */}
            <button
              onClick={() => onNavigate("home")}
              className="w-full mt-8 bg-gradient-to-r from-[#1a1a3e] to-[#2a2a5e] text-white px-8 py-4 rounded-full font-semibold hover:from-[#2a2a5e] hover:to-[#1a1a3e] transition shadow-lg"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f3f9] to-[#e8e4f3] py-12">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <h1 className="mb-8 font-serif text-4xl text-gray-900">Checkout</h1>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Forms */}
          <div className="space-y-6 lg:col-span-2">
            {/* Shipping Information */}
            <div className="p-8 bg-white shadow-lg rounded-2xl">
              <h2 className="flex items-center mb-6 text-2xl font-semibold text-gray-900">
                <Package className="w-6 h-6 mr-2 text-[#d4af37]" />
                Shipping Information
              </h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.firstName
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-[#d4af37]"
                    }`}
                    placeholder="John"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.firstName}
                    </p>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.lastName
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-[#d4af37]"
                    }`}
                    placeholder="Doe"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.lastName}
                    </p>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Email (Optional)
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.email
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-[#d4af37]"
                    }`}
                    placeholder="john.doe@example.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.phone
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-[#d4af37]"
                    }`}
                    placeholder="Phone number"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.city
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-[#d4af37]"
                    }`}
                    placeholder="Tripoli"
                  />
                  {errors.city && (
                    <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Country *
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
                      errors.country
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-[#d4af37]"
                    }`}
                    placeholder="Lebanon"
                  />
                  {errors.country && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.country}
                    </p>
                  )}
                  {!isDomestic && (
                    <p className="mt-1 text-xs text-blue-600">
                      International: we will contact you to coordinate delivery;
                      prepaid via Wish Money / OMT.
                    </p>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Address *
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 resize-none transition ${
                      errors.address
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-[#d4af37]"
                    }`}
                    placeholder="123 Main Street, Apt 4B"
                  />
                  {errors.address && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.address}
                    </p>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Order Notes (Optional)
                  </label>
                  <textarea
                    name="orderNotes"
                    value={formData.orderNotes}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Any special instructions for your order..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Method */}
            <div className="p-8 bg-white shadow-lg rounded-2xl">
              <h2 className="flex items-center mb-6 text-2xl font-semibold text-gray-900">
                <Truck className="w-6 h-6 mr-2 text-[#d4af37]" />
                Delivery Method
              </h2>
              {!isDomestic && (
                <div className="p-4 mb-3 text-sm text-blue-900 border border-blue-200 bg-blue-50 rounded-xl">
                  International order: we will contact you to coordinate
                  delivery time and fee. Delivery options shown below are
                  disabled. Payment via Wish Money / OMT (prepaid).
                </div>
              )}
              <div className="space-y-4">
                <label
                  className={`flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition ${
                    deliveryMethod === "standard"
                      ? "border-[#d4af37] bg-[#fffbf0]"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <input
                      type="radio"
                      name="delivery"
                      value="standard"
                      checked={deliveryMethod === "standard"}
                      onChange={(e) => setDeliveryMethod(e.target.value)}
                      disabled={!isDomestic}
                      className="w-5 h-5 text-[#d4af37] focus:ring-[#d4af37]"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Standard Delivery
                      </p>
                      <p className="text-sm text-gray-600">5-7 business days</p>
                    </div>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">$6.00</p>
                </label>

                <label
                  className={`flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition ${
                    deliveryMethod === "express"
                      ? "border-[#d4af37] bg-[#fffbf0]"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <input
                      type="radio"
                      name="delivery"
                      value="express"
                      checked={deliveryMethod === "express"}
                      onChange={(e) => setDeliveryMethod(e.target.value)}
                      disabled={!isDomestic}
                      className="w-5 h-5 text-[#d4af37] focus:ring-[#d4af37]"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Express Delivery
                      </p>
                      <p className="text-sm text-gray-600">2-3 business days</p>
                    </div>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">$9.00</p>
                </label>
              </div>
            </div>

            {/* Payment Method */}
            <div className="p-8 bg-white shadow-lg rounded-2xl">
              <h2 className="flex items-center mb-6 text-2xl font-semibold text-gray-900">
                <Package className="w-6 h-6 mr-2 text-[#d4af37]" />
                Payment Method
              </h2>
              <div className="space-y-3">
                <label
                  className={`flex items-center justify-between p-4 border-2 rounded-xl transition ${
                    paymentMethod === "cod"
                      ? "border-[#d4af37] bg-[#fffbf0]"
                      : "border-gray-200 hover:border-gray-300"
                  } ${!isDomestic ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <div className="flex items-center gap-4">
                    <input
                      type="radio"
                      name="payment"
                      value="cod"
                      checked={paymentMethod === "cod"}
                      onChange={() => isDomestic && setPaymentMethod("cod")}
                      disabled={!isDomestic}
                      className="w-5 h-5 text-[#d4af37] focus:ring-[#d4af37]"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Cash on Delivery
                      </p>
                      <p className="text-sm text-gray-600">
                        Available in Lebanon only.
                      </p>
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-center justify-between p-4 border-2 rounded-xl transition ${
                    paymentMethod === "wish_omt"
                      ? "border-[#d4af37] bg-[#fffbf0]"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <input
                      type="radio"
                      name="payment"
                      value="wish_omt"
                      checked={paymentMethod === "wish_omt"}
                      onChange={() => setPaymentMethod("wish_omt")}
                      className="w-5 h-5 text-[#d4af37] focus:ring-[#d4af37]"
                    />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Wish Money / OMT (Prepaid)
                      </p>
                      <p className="text-sm text-gray-600">
                        For gifts and international orders.
                      </p>
                    </div>
                  </div>
                </label>

              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky p-8 bg-white shadow-lg rounded-2xl top-24">
              <h2 className="mb-6 text-2xl font-semibold text-gray-900">
                Order Summary
              </h2>

              {cart.length === 0 ? (
                <p className="py-8 text-center text-gray-500">
                  Your cart is empty
                </p>
              ) : (
                <>
                  <div className="mb-6 space-y-4 overflow-y-auto max-h-64">
                    {cart.map((item, index) => (
                      <div
                        key={`${item.id}-${item.selectedSize || index}`}
                        className="flex gap-4"
                      >
                        {" "}
                        {/* UPDATED: Better key */}
                        <img
                          src={item.tinyThumbs?.[0] || item.images?.[0]}
                          alt={item.name}
                          className="object-cover w-16 h-16 rounded-lg"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {item.name}
                          </p>
                          {/* NEW: Display size in checkout summary */}
                          {item.selectedSize && (
                            <p className="text-xs text-gray-500">
                              Size: {item.selectedSize}
                            </p>
                          )}
                          <p className="text-sm text-gray-500">
                            Qty: {item.quantity}
                          </p>
                          <p className="text-sm font-semibold text-gray-900">
                            $
                            {(
                              (item.price +
                                (item.customization?.resizingFee || 0)) *
                              item.quantity
                            ).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="pt-6 space-y-3 border-t border-gray-200">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal</span>
                      <span className="font-semibold">
                        ${baseSubtotal.toLocaleString()}
                      </span>
                    </div>
                    {resizingTotal > 0 && (
                      <div className="flex justify-between text-gray-600">
                        <span>Resizing Fee</span>
                        <span className="font-semibold">
                          ${resizingTotal.toLocaleString()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-gray-600">
                      <span>
                        Delivery (
                        {deliveryMethod === "standard" ? "Standard" : "Express"}
                        )
                      </span>
                      <span className="font-semibold">
                        {isDomestic
                          ? `$${effectiveDeliveryFee.toFixed(2)}`
                          : "TBD"}
                      </span>
                    </div>
                    {!isDomestic && (
                      <p className="text-xs text-blue-600">
                        International order: we will contact you to coordinate
                        delivery and fee.
                      </p>
                    )}
                    <div className="flex justify-between text-gray-600">
                      <span>Payment</span>
                      <span className="font-semibold">
                        {paymentMethod === "cod"
                          ? "Cash on Delivery"
                          : "Wish Money / OMT"}
                      </span>
                    </div>
                    <div className="flex justify-between pt-3 text-xl font-bold text-gray-900 border-t border-gray-200">
                      <span>Total</span>
                      <span>${orderTotal.toLocaleString()}</span>
                    </div>
                  </div>
                  <button
                    onClick={handlePlaceOrder}
                    disabled={isSubmitting || cart.length === 0}
                    className="w-full mt-6 bg-gradient-to-r from-[#1a1a3e] to-[#2a2a5e] text-white py-4 rounded-full font-semibold text-lg hover:from-[#2a2a5e] hover:to-[#1a1a3e] transition shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                        <span>Processing...</span>
                      </div>
                    ) : (
                      "Place Order"
                    )}
                  </button>
                  <p className="mt-4 text-xs text-center text-gray-500">
                    By placing your order, you agree to our Terms of Service
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
